import {assignIssuesToCopilot} from '@github-ui/agent-sessions/utils/copilot-assignment-api'
import {formatAssignmentError} from '@github-ui/agent-sessions/utils/error-handling'
import {isCopilot} from '@github-ui/assignees/copilot-user'
import {commitSetLabelsForLabelableMutation} from '@github-ui/item-picker/commitSetLabelsForLabelableMutation'
import {commitUpdateIssueAssigneesMutation} from '@github-ui/item-picker/commitUpdateIssueAssigneesMutation'
import {commitUpdateIssueProjectsMutation} from '@github-ui/item-picker/commitUpdateIssueProjectsMutation'
import type {UpdateIssueIssueType} from '@github-ui/item-picker/useUpdateIssueIssueTypeMutation'
import type {UpdateIssueMilestone} from '@github-ui/item-picker/useUpdateIssueMilestoneMutation'
import type {AssigneePickerAssignee} from '@github-ui/item-picker-assignee-tsq'
import type {IssueTypePickerIssueType} from '@github-ui/item-picker-issue-type'
import type {LabelPickerLabel} from '@github-ui/item-picker-label-tsq'
import type {MilestonePickerMilestone} from '@github-ui/item-picker-milestone'
import type {ProjectPickerBaseProject as ProjectPickerProject} from '@github-ui/item-picker-project'
import isEqual from 'lodash-es/isEqual'
import {commitMutation, graphql} from 'react-relay'
import type {Environment, IEnvironment, PayloadError} from 'relay-runtime'

import type {DraftIssue} from '../content-preview-types'
import type {Issue} from './use-issue-query'
import type {SavedIssue} from './use-on-save-callback'

export type OnUpdateProps = {
  result: UpdateResult
}

export type ProjectUpdateError = {
  projectId: string
  projectTitle: string
  operation: 'add' | 'remove'
  error: string
}

export type OperationType =
  | 'labels'
  | 'assignees'
  | 'assignCopilot'
  | 'milestone'
  | 'issueType'
  | 'projects'
  | 'content'

export type UpdateResult = {
  successful: OperationType[]
  failed: Array<{
    operation: OperationType
    error: Error
  }>
  issue?: SavedIssue
}

interface IssueComparisonData {
  body?: string | null // Body can be null (treated as empty string for comparison)
  assignees?: AssigneePickerAssignee[]
  labels?: LabelPickerLabel[]
  milestone?: MilestonePickerMilestone | null
  issueType?: IssueTypePickerIssueType | null
  projects?: ProjectPickerProject[]
  hasInaccessibleProject?: boolean
}

export async function commitUpdateIssueMutation({
  environment,
  currentIssue,
  draftIssue,
  proposedChanges,
  canUseCopilotAssignment,
  updateIssueType,
  updateIssueMilestone,
  onCompleted,
}: {
  environment: Environment
  currentIssue: Issue
  draftIssue: DraftIssue
  proposedChanges: IssueComparisonData // Some Draft Issue changes require additional data, so we pass them in separate to the draft
  canUseCopilotAssignment: boolean
  updateIssueType?: UpdateIssueIssueType
  updateIssueMilestone?: UpdateIssueMilestone
  onCompleted?: (response: OnUpdateProps, errors?: readonly PayloadError[]) => void
}): Promise<void> {
  const currentLabelNames =
    currentIssue.labels?.nodes
      ?.filter((label): label is NonNullable<typeof label> => label != null)
      .map(label => label.name) || []

  const proposedLabelNames = proposedChanges.labels?.map(label => label.name) || []

  const labelsChanged =
    proposedChanges.labels !== undefined && !isEqual(currentLabelNames.sort(), proposedLabelNames.sort())

  const milestoneChanged =
    proposedChanges.milestone !== undefined &&
    (proposedChanges.milestone?.title || null) !== (currentIssue.milestone?.title || null)

  const issueTypeChanged =
    proposedChanges.issueType !== undefined && proposedChanges.issueType?.name !== currentIssue.issueType?.name

  // For projects comparison, compare project IDs
  const currentProjectIds =
    currentIssue.projectItems?.nodes
      ?.filter((item): item is NonNullable<typeof item> => item != null)
      .map(item => item.project)
      .filter((project): project is NonNullable<typeof project> => project != null)
      .map(project => project.id)
      .filter(Boolean) || []

  const proposedProjectIds = proposedChanges.projects?.map(project => project.id) || []

  const projectsChanged =
    proposedChanges.projects !== undefined && !isEqual(currentProjectIds.sort(), proposedProjectIds.sort())

  const titleChanged = draftIssue.name !== currentIssue.title
  const bodyChanged = proposedChanges.body !== undefined && proposedChanges.body !== (currentIssue.body || '')

  const metadataPromises: Array<{operation: OperationType; promise: Promise<void>}> = []

  const handleAllMutationsComplete = (successfulOperations: OperationType[]): SavedIssue => {
    // Start with currentIssue data and only apply fields that were successfully mutated

    // Only use proposed title if content mutation succeeded, otherwise keep current
    const finalTitle = successfulOperations.includes('content') ? draftIssue.name : currentIssue.title

    // Only use proposed assignees if assignees mutation succeeded, otherwise keep current
    const finalAssignees =
      successfulOperations.includes('assignees') && proposedChanges.assignees !== undefined
        ? proposedChanges.assignees.map(a => a.login)
        : currentIssue.assignees?.nodes?.map(a => a?.login).filter((login): login is string => Boolean(login)) || []

    return {
      draftType: 'update-issue',
      tag: draftIssue?.tag || '',
      parentTag: draftIssue?.parentTag,
      blockedByTags: draftIssue?.blockedByTags,
      isBlockingTags: draftIssue?.isBlockingTags,
      title: finalTitle,
      number: draftIssue?.issueNumber || currentIssue.number || 0,
      url: currentIssue.url || '',
      repository: {
        databaseId: currentIssue.repository?.databaseId || 0,
        name: currentIssue.repository?.name || '',
        owner: {
          login: currentIssue.repository?.owner.login || '',
        },
      },
      template: draftIssue?.template,
      assignees: finalAssignees,
    }
  }

  // Run specialized mutations for each changed metadata field
  if (labelsChanged) {
    const promise = new Promise<void>((resolve, reject) => {
      commitSetLabelsForLabelableMutation({
        environment,
        input: {
          labelableId: currentIssue.id,
          labels: proposedChanges.labels || [], // Use empty array when removing all labels
          labelableTypeName: 'Issue' as const,
        },
        clientMutationId: draftIssue.messageId,
        onCompleted: () => {
          resolve()
        },
        onError: error => {
          reject(error)
        },
      })
    })
    metadataPromises.push({operation: 'labels', promise})
  }

  const currentAssignees = currentIssue.assignees?.nodes?.filter(
    (assignee): assignee is NonNullable<typeof assignee> => assignee != null,
  )
  const assigneePromise = handleAssigneeUpdates({
    currentIssue,
    currentAssignees,
    selectedAssignees: proposedChanges.assignees,
    canUseCopilotAssignment,
    environment,
    messageId: draftIssue.messageId,
  })
  if (assigneePromise) {
    metadataPromises.push({operation: 'assignees', promise: assigneePromise})
  }

  if (milestoneChanged) {
    const promise = new Promise<void>((resolve, reject) => {
      if (!updateIssueMilestone) {
        reject(new Error('Issue milestone mutation is unavailable'))
        return
      }
      updateIssueMilestone(currentIssue.id, proposedChanges.milestone ?? null, {
        onCompleted: () => resolve(),
        onError: reject,
      })
    })
    metadataPromises.push({operation: 'milestone', promise})
  }

  if (issueTypeChanged) {
    const promise = new Promise<void>((resolve, reject) => {
      if (!updateIssueType) {
        reject(new Error('Issue type mutation is unavailable'))
        return
      }
      updateIssueType(currentIssue.id, proposedChanges.issueType ?? null, {
        onCompleted: () => {
          resolve()
        },
        onError: error => {
          reject(error)
        },
      })
    })
    metadataPromises.push({operation: 'issueType', promise})
  }

  if (projectsChanged) {
    const promise = new Promise<void>((resolve, reject) => {
      // Use helper function to convert issue project data
      const currentProjects = currentIssue.projectItems?.nodes || []

      void (async () => {
        try {
          await updateIssueProjects(
            currentIssue.id,
            proposedChanges.projects || [],
            currentProjects,
            environment,
            draftIssue.messageId,
            proposedChanges.hasInaccessibleProject,
          )
          resolve()
        } catch (projectError: unknown) {
          // Project updates failed
          const errorMessage = projectError instanceof Error ? projectError.message : 'Project update failed'
          const error = new Error(`Project update failed: ${errorMessage}`)
          reject(error)
        }
      })()
    })
    metadataPromises.push({operation: 'projects', promise})
  }

  if (titleChanged || bodyChanged) {
    const promise = new Promise<void>((resolve, reject) => {
      commitMutation(environment, {
        // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
        mutation: graphql`
          mutation updateIssueMutationContentOnlyMutation($input: UpdateIssueInput!) @raw_response_type {
            updateIssue(input: $input) {
              issue {
                id
                title
                body
                bodyVersion
              }
            }
          }
        `,
        variables: {
          input: {
            id: currentIssue.id,
            clientMutationId: draftIssue.messageId,
            ...(titleChanged && {title: draftIssue.name}),
            ...(bodyChanged && {
              body: proposedChanges.body,
              bodyVersion: draftIssue?.bodyVersion,
            }),
          },
        },
        onCompleted: () => {
          resolve()
        },
        onError: error => {
          reject(error)
        },
      })
    })
    metadataPromises.push({operation: 'content', promise})
  }

  const updateResult: UpdateResult = {
    successful: [],
    failed: [],
  }

  if (metadataPromises.length > 0) {
    // Wait for all mutations to complete (they run in parallel)
    const results = await Promise.allSettled(metadataPromises.map(mp => mp.promise))

    for (const [index, result] of results.entries()) {
      const operation = metadataPromises[index]?.operation
      if (!operation) continue

      if (result.status === 'fulfilled') {
        updateResult.successful.push(operation)
      } else {
        updateResult.failed.push({
          operation,
          error: result.reason instanceof Error ? result.reason : new Error('Unknown error'),
        })
      }
    }
  }

  // Handle Copilot assignment if needed and if all other mutations all succeeded
  const assignCopilotOperation = 'assignCopilot'
  try {
    await handleCopilotAssigneeUpdates({
      currentIssue,
      selectedAssignees: proposedChanges.assignees,
      canUseCopilotAssignment,
      hasOtherUpdateErrors: updateResult.failed.length > 0,
      onCompleted: () => updateResult.successful.push(assignCopilotOperation),
    })
  } catch (error) {
    updateResult.failed.push({
      operation: assignCopilotOperation,
      error: error instanceof Error ? error : new Error('Unknown error'),
    })
  }

  if (updateResult.successful.length === 0 && updateResult.failed.length === 0) {
    // No changes were needed, but this is still a successful operation
    const noChangeIssue: SavedIssue = {
      draftType: 'update-issue',
      tag: draftIssue?.tag || '',
      parentTag: draftIssue?.parentTag,
      blockedByTags: draftIssue?.blockedByTags,
      isBlockingTags: draftIssue?.isBlockingTags,
      title: currentIssue.title,
      number: draftIssue?.issueNumber || currentIssue.number || 0,
      url: currentIssue.url || '',
      repository: {
        databaseId: currentIssue.repository?.databaseId || 0,
        name: currentIssue.repository?.name || '',
        owner: {
          login: currentIssue.repository?.owner.login || '',
        },
      },
      template: draftIssue?.template,
      assignees:
        currentIssue.assignees?.nodes?.map(a => a?.login).filter((login): login is string => Boolean(login)) || [],
    }

    if (onCompleted) {
      onCompleted({
        result: {
          successful: [],
          failed: [],
          issue: noChangeIssue,
        },
      })
    }

    return
  }

  const updatedIssue = handleAllMutationsComplete(updateResult.successful)

  if (onCompleted) {
    onCompleted({
      result: {
        successful: updateResult.successful,
        failed: updateResult.failed,
        issue: updatedIssue,
      },
    })
  }
}

const assigneesEqual = (proposed: AssigneePickerAssignee[], current: Array<{readonly login: string}>): boolean => {
  if (proposed.length !== current.length) {
    return false
  }
  const proposedLogins = new Set(proposed.map(assignee => assignee.login))
  return current.every(assignee => proposedLogins.has(assignee.login))
}

function handleAssigneeUpdates({
  currentIssue,
  currentAssignees,
  selectedAssignees,
  canUseCopilotAssignment,
  environment,
  messageId,
}: {
  currentIssue: Issue
  currentAssignees?: Array<{readonly login: string}>
  selectedAssignees?: AssigneePickerAssignee[]
  canUseCopilotAssignment: boolean
  environment: Environment
  messageId: string
}): Promise<void> | void {
  if (selectedAssignees === undefined) {
    return // No permission
  }

  const proposedNonCopilotAssignees = selectedAssignees.filter(assignee => !isCopilot(assignee.login)) ?? []
  const assigneesToSave = canUseCopilotAssignment ? proposedNonCopilotAssignees : selectedAssignees
  const regularAssigneesChanged = !assigneesEqual(assigneesToSave, currentAssignees ?? [])

  if (regularAssigneesChanged) {
    return new Promise<void>((resolve, reject) => {
      commitUpdateIssueAssigneesMutation({
        environment,
        input: {
          issueId: currentIssue.id,
          owner: currentIssue.repository.owner.login,
          repository: currentIssue.repository.name,
          issueNumber: currentIssue.number,
          assignedActors: assigneesToSave, // Use empty array when removing all assignees
          participants: [], // Keep existing participants unchanged
        },
        clientMutationId: messageId,
        onCompleted: () => {
          resolve()
        },
        onError: error => {
          reject(error)
        },
      })
    })
  }
}

async function handleCopilotAssigneeUpdates({
  currentIssue,
  selectedAssignees,
  canUseCopilotAssignment,
  hasOtherUpdateErrors,
  onCompleted,
}: {
  currentIssue: Issue
  selectedAssignees?: AssigneePickerAssignee[]
  canUseCopilotAssignment: boolean
  hasOtherUpdateErrors: boolean
  onCompleted?: () => void
}): Promise<void> {
  if (selectedAssignees === undefined || !canUseCopilotAssignment) {
    // No permission or Copilot assignment not allowed
    return
  }

  const proposedAssigningToCopilot = selectedAssignees?.some(assignee => isCopilot(assignee.login)) ?? false
  if (!proposedAssigningToCopilot) {
    // No Copilot assignment requested
    return
  }

  return (async () => {
    if (hasOtherUpdateErrors) {
      throw new Error('Copilot assignment skipped due to other update error(s)')
    }

    const targetBranch = currentIssue.repository.defaultBranchRef?.name ?? ''
    if (!targetBranch) {
      throw new Error('Cannot assign to Copilot: default branch not found')
    }

    try {
      const response = await assignIssuesToCopilot(
        currentIssue.repository.owner.login,
        currentIssue.repository.name,
        [currentIssue.number],
        {
          instructions: '', // Empty instructions as requested
          targetRepo: `${currentIssue.repository.owner.login}/${currentIssue.repository.name}`,
          targetBranch,
        },
      )
      if (!response.ok) {
        const message = formatAssignmentError(response, 'Assignment failed')
        throw new Error(message)
      }
      onCompleted?.()
    } catch (error) {
      throw error instanceof Error ? error : new Error('Unknown error')
    }
  })()
}

async function updateIssueProjects(
  issueId: string,
  desiredProjects: ProjectPickerProject[],
  currentProjectNodes: NonNullable<NonNullable<Issue['projectItems']>['nodes']>,
  environment: IEnvironment,
  messageId: string,
  hasInaccessibleProject?: boolean,
) {
  const currentProjects = currentProjectNodes.filter(current => current != null && current.project != null)

  const projectsToAdd = desiredProjects.filter(
    desired => !currentProjects.some(current => current?.project.id === desired.id),
  )

  // TODO: Workaround for https://github.com/github/copilot-voyager/issues/786
  // Here we will ignore project removals if there are inaccessible projects
  const projectsToRemove = hasInaccessibleProject
    ? []
    : currentProjects.filter(current => !desiredProjects.some(desired => desired.id === current?.project.id))

  // If no changes in projects, do nothing
  if (projectsToAdd.length === 0 && projectsToRemove.length === 0) {
    return
  }

  const projectErrors: ProjectUpdateError[] = []
  const projectPromises: Array<Promise<void>> = []

  // Process additions individually using commitUpdateIssueProjectsMutation
  for (const project of projectsToAdd) {
    const promise = new Promise<void>(resolve => {
      commitUpdateIssueProjectsMutation({
        environment: environment as Environment,
        projectId: project.id,
        issueId,
        clientMutationId: messageId,
        onCompleted: () => {
          resolve()
        },
        onError: (error: Error) => {
          projectErrors.push({
            projectId: project.id,
            projectTitle: project.title,
            operation: 'add',
            error: error.message,
          })
          resolve() // Resolve anyway to continue with other projects
        },
      })
    })
    projectPromises.push(promise)
  }

  // Process removals individually - we need to find the item IDs from currentProjectItems,
  for (const associatedProject of projectsToRemove) {
    // For removal, we need a different approach since commitDeleteIssueProjectsMutation
    // requires connectionId and is designed for UI contexts.
    // Instead, we'll use a simple GraphQL mutation to remove the item.
    const promise = new Promise<void>(resolve => {
      commitMutation(environment, {
        // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
        mutation: graphql`
          mutation updateIssueMutationDeleteProjectMutation($input: DeleteProjectV2ItemInput!) {
            deleteProjectV2Item(input: $input) {
              deletedItemId
            }
          }
        `,
        variables: {
          input: {
            clientMutationId: messageId,
            itemId: associatedProject?.id, // This should be the project item ID, not project ID
            projectId: associatedProject?.project.id, // The actual project ID
          },
        },
        onCompleted: () => {
          resolve()
        },
        onError: (error: Error) => {
          projectErrors.push({
            projectId: associatedProject?.project.id || 'unknown',
            projectTitle: associatedProject?.project.title || 'unknown',
            operation: 'remove',
            error: error.message,
          })
          resolve() // Resolve anyway to continue with other projects
        },
      })
    })
    projectPromises.push(promise)
  }

  // Wait for all project operations to complete
  await Promise.all(projectPromises)

  // If there were any errors, throw an error with details
  if (projectErrors.length > 0) {
    const errorMessage = `Failed to update ${projectErrors.length} project(s): ${projectErrors
      .map(err => `${err.operation} "${err.projectTitle}" (${err.error})`)
      .join(', ')}`

    interface ProjectUpdateErrorWithDetails extends Error {
      projectErrors: ProjectUpdateError[]
      detailedMessage: string
    }

    const error = new Error('Unable to update issue projects') as ProjectUpdateErrorWithDetails
    error.projectErrors = projectErrors
    error.detailedMessage = errorMessage
    throw error
  }
}
